import { CurrencyType } from './dto'
import { PriceFilter, PriceRange } from './price-creator.dto'

// ---------------------------------------------------------------------------
// Phased price-change scheduling.
//
// VisionAPI is the source of truth for prices; sauna-net.com holds all the
// scheduling intelligence. A "big" requested change (above a configurable
// threshold) can be rolled out gradually as a `PricePlan` made of N `PriceJob`s,
// each of which is a single "set product X price to P at datetime T" command.
//
// Currency mapping on the VisionAPI /api/prices contract: CZK => sale price
// (prodcena), EUR => web price (eshop3).
// ---------------------------------------------------------------------------

export type PriceJobStatus = 'pending' | 'applied' | 'canceled' | 'failed'

/** Derived from the statuses of a plan's jobs. */
export type PricePlanStatus = 'pending' | 'applied' | 'canceled' | 'failed' | 'partial'

export type PricePlanKind = 'single' | 'bulk'

export type PriceChangeSource = 'immediate' | 'force-now' | 'phased-step' | 'recalc'

export type IntervalUnit = 'day' | 'week' | 'month'

/**
 * Which price surface a job/plan targets:
 *  - `web`      — web prices (CZK => prodcena, EUR => eshop3)
 *  - `b2b`      — B2B partner tier prices (D1..D5 / DE1..DE5)
 *  - `catalog`  — catalog price (CZK => katcen, EUR => katcenae)
 *  - `b2b-sale` — B2B partner discount percentages (b2b_sale_1..5); the job's
 *                 `targetPrice` carries the discount **percent**, not a price.
 */
export type BulkPriceChannel = 'web' | 'b2b' | 'catalog' | 'b2b-sale'

/**
 * What a job does when applied:
 *  - `set`             — write a price/discount (the default; uses channel + targetPrice).
 *  - `recalc-*`        — re-derive a family of prices via VisionAPI (no targetPrice; uses `recalcScope`).
 *      `recalc-b2b`      D1..D5/DE1..DE5 from katcen/katcenae × (1 − b2b_sale_i/100)
 *      `recalc-catalog`  katcen/katcenae from prodcena × price-range markup (or RRP)
 *      `recalc-web`      prodcena/eshop3/katcen/katcenae after a settings/RRP change
 *      `recalc-exchange` EUR fields from their CZK counterparts using the gkurzy average over a date window
 */
export type PriceJobOperation = 'set' | 'recalc-b2b' | 'recalc-catalog' | 'recalc-web' | 'recalc-exchange'

/** Tier discount fields that a `b2b-sale` plan can target. Same `b2b_sale_i` drives both D_i and DE_i. */
export const B2B_SALE_FIELDS = ['b2b_sale_1', 'b2b_sale_2', 'b2b_sale_3', 'b2b_sale_4', 'b2b_sale_5'] as const
export type B2bSaleField = (typeof B2B_SALE_FIELDS)[number]

/**
 * Sentinel `productId` for a recalc job that targets a filter / all products rather
 * than one product. The job's `recalcScope` (filter, or empty = everything) is the
 * real scope; this only satisfies the schema's required `productId`.
 */
export const ALL_PRODUCTS = '*'

/** Cadence between two consecutive phased steps. Default: one quarter (3 months). */
export interface PriceInterval {
  value: number
  unit: IntervalUnit
}

/** Map of currency -> amount, used for origin/target prices on a plan. */
export type PriceByCurrency = Partial<Record<CurrencyType, number>>

/**
 * Scope + inputs a `recalc-*` operation forwards to VisionAPI, which runs the
 * corresponding `UPDATE … WHERE $filter_query` over its temp tables (dry-run on 404).
 */
export interface RecalcScope {
  /** Per-product flow: `[productId]`. */
  ids?: string[]
  /** Bulk flow: a price/B2B filter forwarded to the SQL `WHERE`. */
  filter?: Record<string, unknown>
  /** gkurzy exchange-average window start (`$start_date`); synchronizer defaults to the fire date. */
  startDate?: string
  /** gkurzy exchange-average window end (`$end_date`); synchronizer defaults to the fire date. */
  endDate?: string
  /** `recalc-catalog`: price-range markup rows (`#PriceRanges`); default markup 9.5% when absent. */
  priceRanges?: PriceRange[]
}

/** VisionAPI recalc result (dry-run reports `dryRun: true`). */
export interface RecalcResult {
  recalculated: number
  dryRun?: boolean
}

/** One scheduled future price update for one product + currency. */
export interface PriceJob {
  _id?: string
  productId: string
  /** Parent plan; absent only for ad-hoc/standalone jobs. */
  planId?: string
  currency: CurrencyType
  /** Price surface this job targets. Default 'web'. */
  channel?: BulkPriceChannel
  /**
   * Field this job sets: a B2B tier when channel='b2b' (e.g. 'D1', 'DE3') or a
   * discount field when channel='b2b-sale' (e.g. 'b2b_sale_1').
   */
  field?: string
  /** What the job does. Default 'set'. Recalc ops ignore `targetPrice` and use `recalcScope`. */
  operation?: PriceJobOperation
  /** Hard ordering guard: this job waits until the referenced job is `applied` before it runs. */
  dependsOnJobId?: string
  /** Scope/inputs for `recalc-*` operations. */
  recalcScope?: RecalcScope
  /** For `set`: the price; for channel='b2b-sale': the discount **percent** (0–100). Unused for recalc ops. */
  targetPrice: number
  /** Snapshot of the current price when the job was created (for audit/diff). */
  originPrice?: number
  scheduledAt: string | Date
  status: PriceJobStatus
  /** 1-based index of this step within its plan. */
  stepIndex: number
  stepCount: number
  /**
   * Response-only flag: true when this is an EUR step of a `recalcEurFromCzk` plan
   * (price derived from CZK, not directly editable). Populated by `listJobs`; never persisted.
   */
  derivedEur?: boolean
  /** Cron retry bookkeeping. */
  attempts?: number
  lastAttemptAt?: string | Date
  appliedAt?: string | Date
  error?: string
  createdBy?: string
  createdAt?: string | Date
  updatedAt?: string | Date
}

/** Parent grouping of the jobs generated from one big change for one product. */
export interface PricePlan {
  _id?: string
  productId: string
  currencies: CurrencyType[]
  originPrice?: PriceByCurrency
  targetPrice?: PriceByCurrency
  startDate: string | Date
  interval: PriceInterval
  numberOfSteps: number
  kind: PricePlanKind
  /** 'web' or 'b2b' — all jobs of the plan share this channel. Default 'web'. */
  channel?: BulkPriceChannel
  /** Ties together all plans created from one bulk submission. */
  batchId?: string
  /** Queue-after chaining: this plan starts after the referenced plan finishes. */
  dependsOnPlanId?: string
  /** EUR step prices are derived from CZK (CZK ÷ exchangeRate), not entered independently. */
  recalcEurFromCzk?: boolean
  /** CZK-per-EUR rate (koef) captured at plan creation; editable later to recompute EUR steps. */
  exchangeRate?: number
  status: PricePlanStatus
  createdBy?: string
  createdAt?: string | Date
  updatedAt?: string | Date
}

/** Configuration singleton for the scheduling feature. */
export interface PriceSettings {
  _id?: string
  /** Percentage (e.g. 30 = 30%) above which a change is "big" and offered for phasing. */
  thresholdPct: number
  defaultNumberOfSteps: number
  defaultInterval: PriceInterval
  /** Cron retry budget per job before it is marked `failed`. */
  maxRetryAttempts: number
  retryBackoffMinutes: number
  /** Currency rounding precision for intermediate steps. */
  roundingPrecision: number
  /** Floor (%) below which a scheduled B2B discount's resulting partner margin is flagged unprofitable. */
  minPartnerMarginPct: number
  /** Applied/canceled plans & jobs older than this many days are auto-deleted (0 = disabled). */
  retentionDays: number
  updatedAt?: string | Date
}

/** Audit record written for every applied price change. */
export interface PriceChangeLog {
  _id?: string
  productId: string
  currency: CurrencyType
  oldPrice?: number
  newPrice: number
  source: PriceChangeSource
  jobId?: string
  planId?: string
  userId?: string
  result: 'ok' | 'failed'
  error?: string
  appliedAt: string | Date
}

// ---------------------------------------------------------------------------
// Request / response payloads
// ---------------------------------------------------------------------------

export interface PriceStepInput {
  scheduledAt: string
  price: number
}

/** One edited step of a `b2b-sale` plan (discount percent at a datetime). */
export interface SaleStepInput {
  scheduledAt: string
  pct: number
}

/** Per-tier discount target for a `b2b-sale` plan. */
export interface SaleTarget {
  field: B2bSaleField
  targetPct: number
  /** Operator-edited step table; overrides the auto-generated linear schedule. */
  steps?: SaleStepInput[]
}

/** Create a phased plan (single product). Per-currency `steps` override the
 *  auto-generated linear schedule when the operator has edited the table. */
export interface CreateSchedulePayload {
  productId: string
  currencies: CurrencyType[]
  targetPrice: PriceByCurrency
  startDate?: string
  interval?: PriceInterval
  numberOfSteps?: number
  steps?: Partial<Record<CurrencyType, PriceStepInput[]>>
  kind?: PricePlanKind
  batchId?: string
  /** Price surface this plan targets. Default 'web'. 'catalog' writes katcen/katcenae. */
  channel?: BulkPriceChannel
  /** Recalc ops to append after **each** step (e.g. ['recalc-b2b']). Opt-in. */
  recalcAfterEachStep?: PriceJobOperation[]
  /** Gap (minutes) after each step's last set-job before its recalc op fires. Default 60. */
  recalcOffsetMinutes?: number
  /** B2B-sale plans (`channel:'b2b-sale'`): per-tier discount targets instead of currency prices. */
  saleTargets?: SaleTarget[]
  /** Operator acknowledged the queue-after warning for an existing pending plan. */
  acknowledgeQueueAfter?: boolean
  /** B2B-sale: operator overrode the threshold/profitability guardrail ("Vynutit i přes práh"). */
  acknowledgeSaleThreshold?: boolean
  /** Derive EUR steps from CZK (forces currencies to ['CZK','EUR']). */
  recalcEurFromCzk?: boolean
  /** Override the captured CZK-per-EUR rate; if omitted the server captures the latest. */
  exchangeRate?: number
}

/** Manually enqueue a single recalc operation job (the "Přidat přepočet" action). */
export interface CreateRecalcJobPayload {
  operation: PriceJobOperation
  scheduledAt: string
  scope: RecalcScope
  /** Single-product label (optional — a recalc can be bulk via `scope.filter`). */
  productId?: string
  /** Attach to an existing plan so cancelling the plan cancels this recalc. */
  planId?: string
  /** Defer until the referenced job is applied. */
  dependsOnJobId?: string
  /** Logging-only; defaults to 'CZK'. */
  currency?: CurrencyType
}

/** Apply a full new price immediately ("force now") without scheduling. */
export interface ApplyNowPayload {
  productId: string
  currencies: CurrencyType[]
  targetPrice: PriceByCurrency
  /** Price surface to write. Default 'web'; 'catalog' writes katcen/katcenae. */
  channel?: BulkPriceChannel
  /** Derive the EUR price from CZK using `exchangeRate` (or the latest rate). */
  recalcEurFromCzk?: boolean
  exchangeRate?: number
}

export interface DetectionRequest {
  productId: string
  currencies: CurrencyType[]
  targetPrice: PriceByCurrency
  /** Which surface to read the current price from. Default 'web'; 'catalog' reads katcen/katcenae. */
  channel?: BulkPriceChannel
}

export type DetectionReason = 'within-threshold' | 'exceeds-threshold' | 'no-current-price'

export interface CurrencyDetection {
  currency: CurrencyType
  currentPrice?: number
  targetPrice: number
  /** Absolute relative change as a fraction (0.5 == 50%); undefined when no current price. */
  pct?: number
  isBigChange: boolean
  reason: DetectionReason
  /** Suggested linear step prices for this currency (final == exact target). */
  suggestedSteps?: number[]
}

export interface DetectionResult {
  productId: string
  found: boolean
  thresholdPct: number
  detections: CurrencyDetection[]
  hasBigChange: boolean
  /** Present when the product already has a pending plan (queue-after warning). */
  existingPendingPlanId?: string
}

// ---------------------------------------------------------------------------
// B2B partner-discount (b2b_sale_1..5) scheduling — tier-keyed gate.
//
// Two independent warnings per tier: (a) the discount %-change exceeds the
// threshold (big change), and (b) the resulting tier price would make the
// product unprofitable (margin vs evidcena below `minPartnerMarginPct`).
// ---------------------------------------------------------------------------

export interface SaleDetectionRequest {
  productId: string
  /** Tiers the operator wants to change, with the proposed new discount %. */
  saleTargets: { field: B2bSaleField; targetPct: number }[]
}

export interface SaleDetection {
  field: B2bSaleField
  currentPct?: number
  targetPct: number
  /** Absolute relative change of the discount (0.5 == 50%); undefined when no current discount. */
  changePct?: number
  isBigChange: boolean
  /** Resulting tier price = katcen × (1 − targetPct/100). */
  resultingPrice?: number
  /** Resulting partner margin = (resultingPrice − evidcena) / resultingPrice × 100. */
  resultingMarginPct?: number
  /** resultingMarginPct < minPartnerMarginPct (suppressed when profit_check_disabled). */
  isUnprofitable: boolean
}

export interface SaleDetectionResult {
  productId: string
  found: boolean
  thresholdPct: number
  minMarginPct: number
  detections: SaleDetection[]
  /** Present when the product already has a pending plan (queue-after warning). */
  existingPendingPlanId?: string
}

// ---------------------------------------------------------------------------
// VisionAPI /api/prices contract (consumer side)
// ---------------------------------------------------------------------------

export interface VisionPriceRead {
  productId: string
  found: boolean
  prices: Partial<Record<CurrencyType, number | null>>
}

export interface VisionPriceBatchRead {
  total: number
  items: VisionPriceRead[]
}

export interface VisionPriceWriteResult {
  productId: string
  ok: boolean
  changed?: boolean
  price?: number
  currency?: CurrencyType
  error?: string
}

export interface VisionPriceBulkWriteResult {
  total: number
  succeeded: number
  failed: number
  results: VisionPriceWriteResult[]
}

export interface VisionBulkWriteItem {
  productId: string
  price: number
  currency: CurrencyType
}

// ---------------------------------------------------------------------------
// Bulk recalculation (threshold-gated)
//
// A recalculation over a filtered set: VisionAPI computes proposed prices
// (dry-run preview, no apply), sauna-net gates each product+currency by the
// threshold — within-threshold applies immediately, exceeding gets one phased
// plan per product (shared interval). Applying reuses the /api/prices writes.
// ---------------------------------------------------------------------------

export type RecalcKind = 'web'

/** Scope + recalc params forwarded to VisionAPI's dry-run preview. */
export interface RecalcPreviewParams {
  kind: RecalcKind
  filter?: PriceFilter
  ids?: string[]
  /** Recalc inputs VisionAPI needs (exchange dates/rate, priceRanges, …). */
  params?: Record<string, unknown>
}

/** VisionAPI preview: proposed price per product+currency, without applying. */
export interface RecalcPriceDelta {
  current?: number
  proposed: number
}
export interface VisionRecalcPreviewItem {
  productId: string
  prices: Partial<Record<CurrencyType, RecalcPriceDelta>>
}
export interface VisionRecalcPreview {
  total: number
  items: VisionRecalcPreviewItem[]
}

/** How proposed prices are produced for a bulk run. */
export type BulkRecalcSource = 'recalc' | 'percentage'

/** Full preview request from the client (scope + phasing controls). */
export interface BulkRecalcPreviewRequest extends RecalcPreviewParams {
  startDate?: string
  interval?: PriceInterval
  numberOfSteps?: number
  /** 'recalc' (VisionAPI dry-run) or 'percentage' (sauna-net computes current×(1+%)). Default 'recalc'. */
  source?: BulkRecalcSource
  /** For 'percentage': signed percent change (e.g. 10 = +10%, -5 = −5%). */
  percent?: number
  /** For 'percentage': which prices to bump (default ['CZK']). */
  currencies?: CurrencyType[]
}

/** Gated per-currency row returned to the UI (editable before commit). */
export interface BulkRecalcRowCurrency {
  currency: CurrencyType
  current?: number
  proposed: number
  pct?: number
  isBigChange: boolean
  include: boolean
}
export interface BulkRecalcRow {
  productId: string
  currencies: BulkRecalcRowCurrency[]
}
export interface BulkRecalcPreview {
  thresholdPct: number
  batchId: string
  rows: BulkRecalcRow[]
}

/** Commit the (possibly edited) preview. */
export interface BulkRecalcCommit {
  batchId: string
  mode: 'immediate' | 'phase'
  rows: BulkRecalcRow[]
  startDate?: string
  interval?: PriceInterval
  numberOfSteps?: number
}
export interface BulkRecalcResult {
  batchId: string
  applied: number
  planned: number
  failures: { productId: string; currency?: CurrencyType; error: string }[]
}

/** Result of cancelling every plan/step of one bulk batch. */
export interface CancelBatchResult {
  batchId: string
  /** Number of plans in the batch that were affected. */
  plans: number
}

/**
 * One row of the grouped Plány view: either a bulk batch (collapsed to a single
 * header, its products lazy-loaded on expand) or a standalone plan rendered inline.
 */
export interface PlanGroup {
  /** Batch id, or `plan:<planId>` for standalone entries. */
  groupId: string
  batchId?: string
  isBatch: boolean
  productCount: number
  /** Union of the member plans' currencies. */
  currencies: CurrencyType[]
  /** Distinct statuses across the member plans (for the header badge). */
  statuses: PricePlanStatus[]
  startDate: string | Date
  createdAt: string | Date
  /** Only for standalone (non-batch) entries: the single underlying plan, rendered inline. */
  plan?: PricePlan
}

// ---------------------------------------------------------------------------
// Bulk percentage update (from price-creator / b2b-pricing)
//
// Bump a filtered set by a signed percent. The gate is on the % value vs the
// threshold: |%| ≤ threshold ⇒ apply automatically; |%| > threshold ⇒ the
// operator must choose force-apply or schedule (phase per product). VisionAPI
// writes dry-run on HTTP 404 until those endpoints exist.
// ---------------------------------------------------------------------------

/** VisionAPI "update it all by %" contract (force/auto path). */
export interface VisionBulkPercentRequest {
  kind: BulkPriceChannel
  filter?: Record<string, unknown>
  ids?: string[]
  percent: number
  /** web: which currencies to bump. */
  currencies?: CurrencyType[]
  /** b2b: which tiers to bump (e.g. ['D1','DE1']). */
  fields?: string[]
}
export interface VisionBulkPercentResult {
  updated: number
  dryRun?: boolean
}

/** Commit a bulk percentage run from the client. */
export interface BulkPercentageCommit {
  scope: BulkPriceChannel
  filter?: Record<string, unknown>
  ids?: string[]
  percent: number
  /** web: ['CZK'|'EUR']; b2b: tier fields ['D1'..'D5','DE1'..'DE5']. */
  fields: string[]
  mode: 'apply' | 'schedule'
  startDate?: string
  interval?: PriceInterval
  numberOfSteps?: number
}
export interface BulkPercentageResult {
  mode: 'apply' | 'schedule'
  thresholdPct: number
  updated?: number
  planned?: number
  batchId?: string
  failures: { productId: string; error: string }[]
}

/** Count how many products match a bulk-% scope+filter (preview before committing). */
export interface BulkPercentageCountRequest {
  scope: BulkPriceChannel
  filter?: Record<string, unknown>
}
export interface BulkPercentageCountResult {
  total: number
}
